import {
  INTERNAL_REDIRECT_URL_PARAM,
  isStandaloneLocation,
  mergeStandaloneParams,
  sanitizeRedirectUrl,
} from "@repo/shared";
import { useMemo } from "react";
import { useLocation } from "react-router";
import { useStandaloneInstance } from "@/lib/context/standalone-instance-context";

/**
 * QL-33 / QL-261 — the advisor flow-instance page (and its process page) can
 * be opened in "standalone" mode by an external system:
 *
 *   /clients/:id/flows/:instanceId?standalone=true[&internalRedirectUrl=https://…]
 *
 * Standalone mode is a pure UX concern: the app chrome (top navigation,
 * sidebar rail, breadcrumbs) is hidden and the doc buttons are disabled, so
 * the flow can be embedded or linked from a partner system without exposing
 * the rest of the app's navigation. Auth and every business rule are
 * untouched — the same session and API guards apply.
 *
 * The mode is a property of the LINK, not of the instance: nothing is stored,
 * and the same instance opened from the client list shows the normal app. The
 * one rule that keeps it alive is that `?standalone=true` must never disappear
 * while the user is inside the flow — every in-flow URL write goes through
 * `withStandaloneParams`, which merges with the target's own query state.
 *
 * The return target is `internalRedirectUrl`: the value persisted on the
 * instance (set by the partner over the public API, published to the root
 * store by the flow pages via `useStandaloneInstanceSource`) wins over the
 * link-carried query param. Both are sanitized to http(s) before use.
 *
 * QL-262 — NO WAY OUT. In standalone mode the user must never reach another
 * part of QP (client list, client detail, settings, dashboard, …). The rule
 * is enforced centrally by `StandaloneNavigationGuard` (mounted once in
 * `app.tsx`), which intercepts every `navigate()`, `<Link>`, `<Navigate>` and
 * same-origin `<a href>` and classifies the target with
 * `classifyStandaloneNavigation` (packages/shared):
 *
 *   - in-flow (`/clients/:id/flows/:instanceId[/process]`) — proceeds; if the
 *     target dropped `standalone=true` the params are re-added, not blocked;
 *   - logout (`/login`, `/clear`) — proceeds (session round trip);
 *   - external (another origin) — proceeds; new-tab document links likewise;
 *   - anything else — cancelled, and the user is sent to `internalRedirectUrl`
 *     when one exists.
 *
 * What this means for page/component code inside the flow:
 *   - build in-flow URLs with `withStandaloneParams` (the guard corrects a
 *     miss, but do not rely on it — the log warns);
 *   - never navigate to another part of QP after an action (unlock and reopen
 *     stay on `/process` or the wizard); when "leave" is the natural outcome
 *     (submit reaching a hand-off step, cancel advice — QL-737) follow
 *     `autoContinueUrl` when present and otherwise stay put;
 *   - open documents in a NEW tab (`target="_blank"` / `window.open`);
 *   - do not add chrome that links elsewhere (breadcrumbs, client header links)
 *     without hiding it behind `!standalone`.
 */
type StandaloneMode = {
  /** True when the current URL is a flow-instance route with `standalone=true`. */
  standalone: boolean;
  /**
   * Sanitized return target — the instance's persisted `internalRedirectUrl`,
   * else the `internalRedirectUrl` query param — as an absolute http(s) URL
   * safe for `window.location.assign`, or null when absent, invalid, or
   * outside standalone mode.
   */
  internalRedirectUrl: string | null;
  /**
   * The return target that may be followed WITHOUT a click (submit auto-continue,
   * process-page countdown): only the value persisted on the instance — set by
   * the partner over the public API — never the link-carried param, so a
   * crafted link cannot send an advisor off-site on its own. Null when absent,
   * invalid, or outside standalone mode. The link param still powers the
   * Continue button via `internalRedirectUrl`.
   */
  autoContinueUrl: string | null;
  /**
   * Appends the standalone params to an in-app path, merging with any query the
   * path already carries, so navigation inside the flow keeps the mode. Returns
   * the path unchanged outside standalone mode. The ONLY way to build an
   * in-flow URL.
   */
  withStandaloneParams: (path: string) => string;
};

export function useStandaloneMode(): StandaloneMode {
  const { pathname, search } = useLocation();
  const instance = useStandaloneInstance();
  const persistedRedirectUrl = instance?.internalRedirectUrl ?? null;

  return useMemo(() => {
    const standalone = isStandaloneLocation(pathname, search);
    const paramRedirectUrl = new URLSearchParams(search).get(INTERNAL_REDIRECT_URL_PARAM);
    const origin = window.location.origin;
    const autoContinueUrl = standalone ? sanitizeRedirectUrl(persistedRedirectUrl, origin) : null;
    const internalRedirectUrl = standalone
      ? (autoContinueUrl ?? sanitizeRedirectUrl(paramRedirectUrl, origin))
      : null;

    const withStandaloneParams = (path: string) =>
      standalone ? mergeStandaloneParams(path, paramRedirectUrl) : path;

    return { standalone, internalRedirectUrl, autoContinueUrl, withStandaloneParams };
  }, [pathname, search, persistedRedirectUrl]);
}
